import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux'

import { Toolbar, ToolbarItem, ToolbarSpacer, Button, DropDownButton, DropDownButtonItem } from "@progress/kendo-react-buttons";
import download from 'downloadjs';

import ImportFromInstanceDialog from 'views/widgets/ImportFromInstanceDialog.jsx';
import SelectRevisionDialog from 'views/widgets/SelectRevisionDialog.jsx';
import ImportDialog from 'views/widgets/ImportDialog.jsx';
import ImportResultsDialog from 'views/widgets/ImportResultsDialog.jsx';

import ConfirmationDialog from "views/widgets/ConfirmationDialog.jsx";

import { addMessage } from '../../messages/messageSlice';
import useSession from '../../hooks/useSession';
  
export default function PagingGridToolbar(props) {
  
  	const { 
		type, label, url, columns, sort, selected, hasAdd, hasAddFromInstance, handleAddFromInstanceError,
		hasView, hasEdit, hasDelete, hasCopy, extraButtons, hasMultipleRevisions,
  		includeExport, updateMask, getFullItems, refresh, hideButtons, restoreDefaultLayout,
  		showGrid=true, gridViewChanged, includeToggle, searchWidget, bulkSelect, bulkOperation, setBulkOperation,
  		queryParams } = props;
  	
  	const navigate = useNavigate();
  	const [visibleAddFromInstancePrompt, setVisibleAddFromInstancePrompt] = useState(false);
  	const [selectRevisionPromptState, setSelectRevisionPromptState] = useState({ show: false });
  	const [visibleDeletePrompt, setVisibleDeletePrompt] = useState(false);
  	const [visibleImportDialog, setVisibleImportDialog] = useState(false);
  	const [importResultsDialog, setImportResultsDialog] = useState({show: false});

  	const dispatch = useDispatch();
  	const { hasAddPermission, hasViewPermission, hasEditPermission, hasDeletePermission, hasExportPermission } = useSession();
  	
  	const hasDeletePermissions = (selected) => {
		  let hasPermission = true;
		  for (let i = 0; i < selected.length; i++) hasPermission &= hasDeletePermission(type, selected[i]);
		  return hasPermission;
	};
 
    const clickAddButton = () => {	
		navigate(`/${type}/add?${new URLSearchParams(queryParams).toString()}`);
    };
    
    const clickAddFromInstanceButton = () => {
		toggleAddFromInstancePrompt();
	}
	
	const toggleAddFromInstancePrompt = () => {		
      	setVisibleAddFromInstancePrompt(!visibleAddFromInstancePrompt);
    };
    
    const addFromInstance = (id, name) => {
		toggleAddFromInstancePrompt();
		setSelectRevisionPromptState({ 
			show: true, 
			id: id, 
			name: name, 
			pickFromOtherInstance: true, 
			excludeLiveRevision: true, 
			okCallback: updateToRevision 
		});
	};
	
	const updateToRevision = (id, revisionId, instanceId) => {
		let options = {
			credentials: 'include',
          	method: 'POST',
          	headers: { 'Content-Type': 'application/json' }
        }
        updateMask(true);
        let fullUrl = '/api/' + type + '/' + id + '/updateToRevision/' 
      			      + revisionId + '?sourceInstanceId=' + instanceId;
      	fetch(API_URL + fullUrl, options
      	).then((response) => {
           	if (response.ok) { return response.json(); }
           	else { throw new Error(response.status); }
        }).then(() => {
           	updateMask(false);
           	setSelectRevisionPromptState({ show: false });
           	dispatch(addMessage({ type: 'success', msg: 'Successfully added ' + type}));
           	refresh();
        }).catch((err) => {
			updateMask(false);
			setSelectRevisionPromptState({ show: false });
			if (handleAddFromInstanceError)
				handleAddFromInstanceError(err, fullUrl, refresh)
			else {
	          	let errMsg = (err.message === '403') ? ("Unauthorized session!") : ("Failed to add " + type + "(s).  If this problem persists, please contact Support.");
				dispatch(addMessage({ type: 'error', msg: errMsg}));
			}              	
		});	
	};
    
    const clickViewButton = () => {
      	navigate('/' + type + '/view/' + selected[0]);
    };
    
    const clickEditButton = () => {
      	navigate('/' + type + '/modify/' + selected[0]);
    };
    
    const clickCopyButton = () => {
		if (hasMultipleRevisions(selected[0])) {
			let name = getFullItems([ selected[0] ])[0].name;
			setSelectRevisionPromptState({ 
				show: true, 
				id: selected[0], 
				name: name,  
				okCallback: copyRevision });
		} else 
			copyRevision(selected[0]);
	};
	
	const copyRevision = (id, revisionId, instanceId) => {
		let urlParams = (revisionId ? '?revisionId=' + revisionId + '&instanceId=' + instanceId : '');
      	let options = {
			credentials: 'include',
          	method: 'PUT',
          	headers: { 'Content-Type': 'application/json' }
        }
        updateMask(true);
      	fetch(API_URL + '/api/' + type + '/' + id + '/copy' + urlParams, options
      		).then((response) => {
            	if (response.ok) { return response.json(); }
            	else { throw new Error(response.status); }
          	}).then(() => {
              	updateMask(false);
              	setSelectRevisionPromptState({ show: false });
              	dispatch(addMessage({ type: 'success', msg: 'Successfully copied ' + label}));
              	refresh();
            }).catch((err) => {
              	let errMsg = (err.message === '403') ? ("Unauthorized session!") : ("Failed to copy " + label + ".  If this problem persists, please contact Support.");
              	updateMask(false);
              	setSelectRevisionPromptState({ show: false });
				dispatch(addMessage({ type: 'error', msg: errMsg}));              	
            });
    };
    
    const clickDeleteButton = () => {
		toggleDeletePrompt();
    };
    
    const deleteSelected = () => {
		const bulk = bulkOperation ? "bulk" : "";
		const method = bulkOperation ? "POST" : "DELETE";
		const params = bulkOperation ? "?searchStr=" : "";
		
        let options = {
			credentials: 'include',
			mode: 'cors',
          	method: method,
          	headers: { 'Content-Type': 'application/json' },          	
        }
        
        if(!bulkOperation) options.body = JSON.stringify(selected);

      	toggleDeletePrompt();
        updateMask(true);
        fetch(API_URL + '/api/' + type + '/' + bulk + 'delete' + params, options
        	).then((response) => {
            	if (response.ok) {										 
					return response.json(); 
				} else {
					throw new Error(response.status, response.message); 
				}
          	}).then((response) => {					  			  
				let message = { type: 'success', msg: 'Successfully deleted ' + label };
				let refreshPage = true;
				
				if(response.message && response.message.includes("WARN:")) {
					message = { type: 'warning', msg: response.message.replace('WARN:', '') };
					refreshPage = false;
				}
              		
              	updateMask(false);  	              	        	
              	dispatch(addMessage(message));
              	refreshPage && refresh(true);
			}).catch((err) => {
				let errMsg = (err.message === '403') ? ("Unauthorized session!") 
				           : (err.message === '412') ? (`${label} are currently in use and cannot be deleted.`)
				           : ("Failed to delete " + label + ".  If this problem persists, please contact Support.");
				updateMask(false);
				dispatch(addMessage({ type: 'error', msg: errMsg }));
			});
            
        if(setBulkOperation) setBulkOperation(false);
    };
    
    const toggleDeletePrompt = () => {		
      	setVisibleDeletePrompt(!visibleDeletePrompt);
    };
    
    const importExportClicked = (item) => {
		if (item.item.text == 'Import') importClicked();
		else exportClicked();
	};
	
	const importClicked = () => {
		setVisibleImportDialog(true);
	};
	
	const importObject = (filename) => {
		setVisibleImportDialog(false);
		let options = {
			credentials: 'include',
			mode: 'cors',
          	method: 'POST',
          	headers: { 'Content-Type': 'application/json' }
        }
        updateMask(true);
		fetch(API_URL + '/api/importexport/import?type=' + type + '&filename=' + filename, options
        ).then((response) => {
           	if (response.ok) { return response.json(); }
           	else { throw new Error(response.status); }
        }).then((json) => {
			setImportResultsDialog({ show: true, logs: json.imported.importLog });
           	updateMask(false);
           	refresh();
        }).catch((err) => {
           	let errMsg = (err.message === '403') ? ("Unauthorized session!") : ("Failed to import " + label + ".  If this problem persists, please contact Support.");
           	updateMask(false);
          	dispatch(addMessage({ type: 'error', msg: errMsg}));
        });		
	};
	
	const exportClicked = () => {
		if (hasMultipleRevisions(selected[0])) {
			let name = getFullItems([ selected[0] ])[0].name;
			setSelectRevisionPromptState({ 
				show: true, 
				id: selected[0], 
				name: name,  
				okCallback: exportRevision });
		} else 
			exportRevision(selected[0]);
	}
	
	const exportRevision = (id, revisionId, instanceId) => {
		let urlParams = (revisionId ? '?revisionId=' + revisionId + '&instanceId=' + instanceId : '');
		let options = {
			credentials: 'include',
			mode: 'cors',
          	method: 'POST',
          	headers: { 'Content-Type': 'application/json' }
        }
        updateMask(true);
		fetch(API_URL + '/api/importexport/export/' + type + '/' + selected[0] + urlParams, options
        ).then((response) => {
           	if (response.ok) { return response.json(); }
           	else { throw new Error(response.status); }
        }).then((json) => {
           	updateMask(false);
			setSelectRevisionPromptState({ show: false });
           	downloadExport(json.message);
        }).catch((err) => {
           	let errMsg = (err.message === '403') ? ("Unauthorized session!") : ("Failed to export " + label + ".  If this problem persists, please contact Support.");
           	updateMask(false);
            setSelectRevisionPromptState({ show: false });           	
          	dispatch(addMessage({ type: 'error', msg: errMsg}));
        });
	};
	
	const downloadExport = (filename) => {
		updateMask(true);
		let options = {
			credentials: 'include',
			method: 'GET',
			headers: { 
				'Content-Type': 'application/json',
				'Content-Disposition' : 'attachment; filename=' + filename 
			}
		}
		fetch(API_URL + '/api/importexport/downloadExport?filename=' + filename, options
  		).then((response) => {
			if (response.ok) { return response.blob(); }
			else { throw new Error(response.status); }
		}).then((blob) => {
			updateMask(false);
    		download(blob, filename, "text/plain");
  		}).catch((status) => {
			let errMsg = (status.message === '403') ? ("Unauthorized session!") : ("Failed to download export file.  If this problem persists, please contact Support.");
			updateMask(false);
			dispatch(addMessage({type: 'error', msg: errMsg}));  
		});
	}

  	let buttons = [];
  	
  	if (!hideButtons) {
	  	if (hasAdd && hasAddPermission(type)) 
	  		buttons.push({ type : 'button', id: 'add', icon: 'plus', title: 'Add', onClick: clickAddButton });
	  	if (hasAddFromInstance && hasAddPermission(type)) 
	  		buttons.push({ type: 'button', id: 'add-from-instance', icon: 'download', title: 'Add From Instance', onClick: clickAddFromInstanceButton });
	  	if (selected.length == 1) {
	      	if (hasView && hasViewPermission(type, selected[0]))
	      		buttons.push({ type : 'button', id: 'view', icon: 'eye', title: 'View', onClick: clickViewButton });
	    	if (hasEdit && hasEditPermission(type, selected[0]))
	      		buttons.push({ type : 'button', id: 'edit', icon: 'pencil', title: 'Edit', onClick: clickEditButton });
	      	if (hasCopy && hasAddPermission(type))
	      		buttons.push({ type : 'button', id: 'copy', icon: 'clone', title: 'Copy', onClick: clickCopyButton });
		}
		if (selected.length >= 1) {
	    	if (hasDelete && hasDeletePermissions(selected))
	    		buttons.push({ type: 'button', id: 'delete', icon: 'trash', title: 'Delete', onClick: clickDeleteButton });
	  	}      		
	  	
	  	if (extraButtons) {
		  	for (let i = 0; i < extraButtons.length; i++) {
				if (extraButtons[i].hasPermission(selected, getFullItems)) { 
					buttons.push(extraButtons[i]);
				}
			}
		}
	};

	const buttonElems = buttons.map((button) => {
	  const isButton = button.type === 'button';
	  const isDropdownButton = button.type === 'drop-down-button';
	  	
	  if(isDropdownButton){
		var dropDownButtonItems = button.items.map((item) => 
	    	<DropDownButtonItem 
	    		key={button.id + '-' + item.id}
  				iconClass={"fa fa-fw fa-" + item.icon} 
  				text={item.text}
  			/>
    	);
		
		return (
			<ToolbarItem
				key={button.id + 'dropdown-toolbar'}>
	          <DropDownButton 
	            key={button.id + 'dropdown'} 
	            iconClass={"fa fa-fw fa-" + button.icon} 
	            title={button.title}
	            className={button.className}
	            onItemClick={button.onClick}
	          >
	            {dropDownButtonItems}
	          </DropDownButton>
	        </ToolbarItem>
		)
	  } else {
		return (
			<Button 
		          key={button.id + 'button'} 
		          iconClass={"fa fa-fw fa-" + button.icon} 
		          title={button.title} 
		          onClick={button.onClick} 
		          className={button.className}
		    />)
	  }
	});

  	
  	const showToolbar = buttonElems.length > 0 || includeToggle || includeExport || searchWidget;
  	const showImport = includeExport && hasAddPermission(type);
  	const showExport = includeExport && (selected.length == 1 && hasExportPermission(type, selected[0]));
  	
	return (
    	<div>
    		{showToolbar &&
	    	<Toolbar className="ccm-paging-grid-toolbar">
	    		{buttonElems}
				<ToolbarSpacer />			
				{bulkSelect && bulkSelect()}
				<ToolbarSpacer />
				{searchWidget && searchWidget()}	
				{includeToggle && <Button
					key="card-view"
					title="Toggle Card View"
					className={showGrid ? "ccm-toggle-button-off" : "ccm-toggle-button-on"} 
					iconClass="fa fa-fw fa-small fa-th-large"
					togglable={true}
					onClick={gridViewChanged}
				/>}
				<Button 
					iconClass="fa fa-fw fa-small fa-window-maximize" 
					title="Restore Layout" 
					onClick={() => restoreDefaultLayout()}
				>					
				</Button>
				{includeExport && (showImport || showExport) &&
		 		   	<ToolbarItem>
		        		<DropDownButton 
		        			key="import-export-button"
		        			title="Import/Export"
		        			iconClass="fa fa-fw fa-cog"
		        			onItemClick={importExportClicked}
		        		>
		        			{showImport &&
		        			<DropDownButtonItem 
		          				iconClass="fa fa-fw fa-upload-cloud" 
		          				text="Import" 
		          			/>}
		          			{showExport && 
		          			<DropDownButtonItem 
		          				iconClass="fa fa-fw fa-download-cloud" 
		          				text="Export" 
		          			/>
		          			}
		        		</DropDownButton>
		      		</ToolbarItem>
		      	}
	    	</Toolbar>}
	    	{visibleImportDialog && (
				<ImportDialog 
					label={label}
					okCallback={importObject}
					cancelCallback={() => setVisibleImportDialog(false)}					 
				/>
			)}
			{importResultsDialog.show && (
				<ImportResultsDialog
					label={label}
					logs={importResultsDialog.logs}
					closeCallback={() => { setImportResultsDialog({ show: false}); }}
				/>
			)}
			{visibleAddFromInstancePrompt && (
				<ImportFromInstanceDialog
					label={label}
					type={type}
					url={url}
					columns={columns}
					sort={sort}
					okCallback={addFromInstance}
					cancelCallback={() => setVisibleAddFromInstancePrompt(false)}
				/>
			)}
			{selectRevisionPromptState.show && (
				<SelectRevisionDialog
					url={url}
					id={selectRevisionPromptState.id}
					name={selectRevisionPromptState.name}
					pickFromOtherInstance={selectRevisionPromptState.pickFromOtherInstance}
					excludeLiveRevision={selectRevisionPromptState.excludeLiveRevision}
					okCallback={selectRevisionPromptState.okCallback}
					cancelCallback={() => setSelectRevisionPromptState({ show: false })}
				/>
			)}
	    	{visibleDeletePrompt && (
		        <ConfirmationDialog
		        	title={"Delete " + label}
		        	prompt={`Are you sure you want to permanently delete the selected ${label}? The action cannot be undone.`}
		        	yesCallback={deleteSelected}
		        	noCallback={toggleDeletePrompt}
		        />		        
	      	)}    
      	</div>	
  	);
}